import React from 'react';

import once from 'lodash/once';

import { useApplicationContext } from '@common/react/components/Core/Application/Application';
import BaseSecureRouteWrapper, {
	CanAccessDelegate,
	BaseSecureRoutesProviderContext,
	BaseSecureRouteWrapperProps,
} from '@common/react/components/Routes/SecureRoute/BaseSecureRouteWrapper';
import { MenuItem } from '@common/react/objects/MenuItem';

import { useCompanyFeatureProviderContext } from '@app/components/UI/CompanyFeaturesSetter/CompanyFeaturesSetter';
import { RoutesPropsMap } from '@app/objects/SecureRoutesPropsPagesMap';
import { User, UserFavorite, UserRole } from '@app/objects/User';
import { useUserGroupContext } from '@app/components/UI/UserGroupProvider/UserGroupProvider';
import { dashboardMenu, DashboardMenuItem } from '@app/components/Routes/DashboardMenu';
import getAccessibleMenu from '@app/components/Layouts/DashboardLayout/getAccessibleMenu';
import getRootCheckAdminMenu from '@app/components/Layouts/DashboardLayout/useRootCheckAdminMenu';
import { useUserFavoritesProviderContext } from '@app/components/UI/UserFavoritesProvider/UserFavoritesProvider';

interface SecureRoutesProviderContext extends BaseSecureRoutesProviderContext {
	menu: Array<MenuItem>;
}

interface SecureRouteWrapperProps extends BaseSecureRouteWrapperProps<User, RoutesPropsMap> {
	children: React.ReactNode | ((context: SecureRoutesProviderContext) => React.ReactNode);
}

const createSecureRoutesProviderContext = once(
	() => React.createContext({} as SecureRoutesProviderContext),
);

export const useSecureRoutesProviderContext: () => SecureRoutesProviderContext = () => React.useContext(createSecureRoutesProviderContext());

export const SecureRouteWrapper: React.FC<SecureRouteWrapperProps> = (props: SecureRouteWrapperProps) => {
	const SecureRoutesProviderContext = createSecureRoutesProviderContext();

	const {
		children,
		...rest
	} = props;

	const { getUser } = useApplicationContext();
	const { checkFeatureAccess } = useCompanyFeatureProviderContext();
	const { checkUserGroupAccess } = useUserGroupContext();
	const { checkUserAccess } = useApplicationContext();
	const userFavoritesContext = useUserFavoritesProviderContext();
	const userFavorites = userFavoritesContext?.state?.items || [];

	const canRoleAccess = (route: RoutesPropsMap) =>
		!route.roles || (route.roles.length > 0 && checkUserAccess(route.roles));

	const canFeatureAccess = (route: RoutesPropsMap) =>
		!route.features || !route.features.length || (route.features.length > 0 && checkFeatureAccess(route.features));

	const canUserGroupAccess = (route: RoutesPropsMap) =>
		!route.userGroupTypes || !route.userGroupTypes.length
		|| (route.userGroupTypes.length > 0 && checkUserGroupAccess(route.userGroupTypes));

	const canAccess: CanAccessDelegate<User, RoutesPropsMap> = (user, route) => {
		return Boolean(!route || (user && (user.root
			|| (canRoleAccess(route) && canFeatureAccess(route) && canUserGroupAccess(route)))));
	};

	const checkFeaturesAccess = (value: DashboardMenuItem) => {
		if (value.features && value.features.length > 0) {
			return checkFeatureAccess(value.features, !!value.anyFeature);
		}
		return true;
	};

	const setFavorite = (menu: DashboardMenuItem, userFavorites: Array<UserFavorite>): DashboardMenuItem => ({
		...menu,
		isFavorite: userFavorites.filter((q) => q.url === menu.path).length > 0,
		children: menu.children && {
			...menu.children,
			list: menu.children.list.map((q) => setFavorite(q, userFavorites)),
		},
	});

	const user = getUser<User>();
	const rootCheckAdminMenu = getRootCheckAdminMenu();

	return (
		<BaseSecureRouteWrapper<User, RoutesPropsMap> canAccess={canAccess} {...rest}>
			{({ canAccess }) => {
				const menu = React.useMemo(() => {
					if (!user) {
						return [];
					}

					if (user.role === UserRole.External) {
						return [{
							path: '/document-list',
							name: 'Documents',
						}];
					}

					let result = user?.root ? (rootCheckAdminMenu || dashboardMenu)
						: getAccessibleMenu(
							checkUserAccess,
							rootCheckAdminMenu,
							checkFeaturesAccess,
							canAccess,
						);

					if (!userFavorites) {
						return result;
					}

					result = result.map((menu) => setFavorite(menu, userFavorites));

					return result;
				}, [user, user.role, userFavorites, rootCheckAdminMenu]);

				const context = {
					canAccess,
					menu,
				};

				return <SecureRoutesProviderContext.Provider value={context}>
					{typeof children === 'function' ? children(context) : children}
				</SecureRoutesProviderContext.Provider>;
			}}
		</BaseSecureRouteWrapper>
	);
};

export default SecureRouteWrapper;
